/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import {
  createSettingCustomAlert,
  delSettingCustomAlert,
  fetchSettingCustomAlert,
  fetchSlackChannelList,
  updateSettingCustomAlert,
} from '../../../states/containers/setting/systemSlice'
import { fetchTaskCandidateServices } from '../../../states/containers/task/taskSlice'
import { useAlert } from 'react-alert'
import ConfirmationModal from '../../components/setting/customAlert/ConfirmationModal'
import CustomAlertModal from '../../components/setting/customAlert/CustomAlertModal'
import { CustomAlert, RequestPatchSettingCustomAlert, RequestPostSettingCustomAlert, RoleMember, RoleTeam, SettingCustomAlert } from '../../../states/types/setting'
import CustomAlertDetail from '../../components/setting/customAlert/CustomAlertDetail'
import CreateCustomAlertWizardModal from '../../components/setting/customAlert/CreateCustomAlertWizardModal'
import GroupButton from '../../components/common/GroupButton'
import LoadingSquare from '../../components/common/LoadingSquare'

interface CustomAlertModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  customAlertDetail: SettingCustomAlert | undefined,
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const CustomAlert: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [customAlertList, setCustomAlertList] = useState<SettingCustomAlert[]>([])
  const [customAlertModalState, setCustomAlertModalState] = useState<CustomAlertModalState>({
    display: false,
    type: "",
    id: undefined,
    customAlertDetail: undefined,
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [isOpenWizard, setIsOpenWizard] = useState<boolean>(false)

  const [teams, setTeams] = useState<RoleTeam[]>([])
  const [members, setMembers] = useState<RoleMember[]>([])
  const [candidateServices, setCandidateServices] = useState<{ id: number, name: string }[]>([])
  const [isCandidateServicesLoading, setIsCandidateServicesLoading] = useState<boolean>(true)
  const [slackChannels, setSlackChannels] = useState<{ id: string, is_private: boolean, name: string}[]>([])

  // custom alert modal
  const openCustomAlertModal = (event?: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) => {
    const type = event?.currentTarget.dataset.type as string || "create"
    const id = Number(event?.currentTarget.dataset.id)
    const customAlertDetail = customAlertList.find((v) => v.id === id)
    setCustomAlertModalState({
      ...customAlertModalState,
      display: true,
      type: type,
      id: id,
      customAlertDetail: customAlertDetail,
    })
  }

  const closeCustomAlertModal = () => {
    setCustomAlertModalState({
      ...customAlertModalState,
      display: false,
    })
  }

  const duplicateCustomAlert = (id: number) => {
    const customAlertDetail = customAlertList.find((v) => v.id === id)
    setCustomAlertModalState({
      ...customAlertModalState,
      display: true,
      type: "duplicate",
      id: id,
      customAlertDetail: customAlertDetail,
    })
  }

  const deleteCustomAlert = (id: number) => {
    dispatch(
      delSettingCustomAlert(
        {
          id: id,
        },
        () => {
          setCustomAlertList((cur) => cur.filter((v) => v.id !== id))
          alert.success("アラートを削除しました")
        },
      ),
    )
  }

  const changeAlertStatus = (id: number, active: boolean) => {
    dispatch(
      updateSettingCustomAlert(
        {
          id: id.toString(),
          active: active,
        },
        () => {
          setCustomAlertList((cur) => cur.map((v) => (v.id === id ? { ...v, active: active } : v)))
          alert.success(`アラートを${active ? "開始" : "停止"}しました`)
        },
      ),
    )
  }

  const openConfirmationModal = (type: string, id: number) => {
    const customAlert = customAlertList.find((v) => v.id === id)
    let callback = () => {
      // none
    }
    switch (type) {
      case "stop":
        callback = () => changeAlertStatus(id, false)
        break
      case "delete":
        callback = () => deleteCustomAlert(id)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: customAlert?.name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const addCustomAlert = (params: RequestPostSettingCustomAlert, callback?: VoidFunction) => {
    dispatch(
      createSettingCustomAlert(
        params,
        () => {
          dispatch(
            fetchSettingCustomAlert(
              (result) => {
                setCustomAlertList(result.custom_alerts)
              },
            ),
          )
          alert.success("アラートを作成しました")
          if (callback) callback()
        },
      ),
    )
  }

  const modCustomAlert = (params: RequestPatchSettingCustomAlert) => {
    dispatch(
      updateSettingCustomAlert(
        params,
        () => {
          dispatch(
            fetchSettingCustomAlert(
              (result) => {
                setCustomAlertList(result.custom_alerts)
              },
            ),
          )
          alert.success("アラートを更新しました")
        },
      ),
    )
  }

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(
      fetchTaskCandidateServices(
        (result) => {
          setCandidateServices(result.candidate_services)
          setIsCandidateServicesLoading(false)
        },
      ),
    )
    dispatch(
      fetchSettingCustomAlert(
        (result) => {
          setCustomAlertList(result.custom_alerts)
          setTeams(result.teams)
          setMembers(result.members)
          setIsLoading(false)
          if (result.slack_integrated) {
            dispatch(fetchSlackChannelList(
              (slackResult) => {
                setSlackChannels(slackResult)
              },
            ))
          }
        },
      ),
    )
  }, [login.accessToken])

  return (
    <>
      <section className="setting_page">
        <Helmet>
          <title>Qasee - 設定 - カスタムアラート</title>
        </Helmet>
        <section className="breadcrumb">
          設定
          &nbsp;&gt;&nbsp;
          カスタムアラート
          &nbsp;
        </section>
        <section className="page_header">
          <h1>
            カスタムアラート
          </h1>
        </section>
        <section className="page_description">
          <p>
            カスタムアラートを設定すると、条件に満たしたユーザーがいる時、アラートメールが送信されます。
          </p>
        </section>
        <section className="page_contents content-area">
          {
            isLoading ? (
              <LoadingSquare>
                <>
                  情報を取得しています…
                </>
              </LoadingSquare>
            ) : (
              <div className="content-block">
                <div className="custom_alert_button">
                  <GroupButton
                    list={[
                      {
                        icon: "plus",
                        text: "簡単アラート作成",
                        callback: () => setIsOpenWizard(true),
                      },
                      {
                        icon: "plus",
                        text: "手動でアラートを作成",
                        callback: () => openCustomAlertModal(),
                      },
                    ]}
                  />
                </div>
                <div
                  className="block-detail"
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  {
                    customAlertList.length ? (
                      customAlertList.map((v) => (
                        <React.Fragment key={`custom_alert_detail_${v.id}`}>
                          <CustomAlertDetail
                            customAlert={v}
                            openCustomAlertModalCallback={(event) => openCustomAlertModal(event)}
                            duplicateCustomAlertCallback={(id) => duplicateCustomAlert(id)}
                            openConfirmationModalCallback={(type, id) => openConfirmationModal(type, id)}
                            changeAlertStatusCallback={(id, active) => changeAlertStatus(id, active)}
                            teamList={teams}
                          />
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            alignItems: "center",
                            gap: "24px",
                            fontSize: "30px",
                            color: "#CCCCCC",
                          }}
                        >
                          カスタムアラートはありません
                          <button type="button" className="button" onClick={openCustomAlertModal}>
                            アラートを作成
                          </button>
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
            )
          }
        </section>
        {/* 詳細モーダル */}
        <CustomAlertModal
          customAlertModalState={customAlertModalState}
          closeCallback={closeCustomAlertModal}
          createCallback={(params) => addCustomAlert(params)}
          updateCallback={(params) => modCustomAlert(params)}
          teamList={teams}
          memberList={members}
          candidateServices={candidateServices}
          isCandidateServicesLoading={isCandidateServicesLoading}
          slackChannels={slackChannels}
        />
        {/* 確認モーダル */}
        <ConfirmationModal
          confirmationModalState={confirmationModalState}
          closeCallback={closeConfirmationModal}
        />
        <CreateCustomAlertWizardModal
          closeCallback={() => setIsOpenWizard(false)}
          addCallback={(params, callback) => addCustomAlert(params, callback)}
          teamList={teams}
          memberList={members}
          candidateServices={candidateServices}
          isCandidateServicesLoading={isCandidateServicesLoading}
          isDisplay={isOpenWizard}
        />
      </section>
    </>
  )
}

export default CustomAlert
